import { Link } from "react-router-dom";
import { Breadcrumbs } from "@/src/components/SiteChrome";
import { DocumentContent, RegulatoryBanner } from "@/src/components/ContentBlocks";
import { content, course, labs, weeks } from "@/src/lib/content";

const journey = [
  { number: "01", weeks: "Weeks 1 to 2", phase: "Understand", title: "Interrogate systems and claims", text: "Build AI literacy, distinguish modalities, and challenge fluent outputs with evidence." },
  { number: "02", weeks: "Weeks 3 to 6", phase: "Assess", title: "Trace harm through the lifecycle", text: "Work with incidents, fairness, privacy, explanation, robustness, and model evidence." },
  { number: "03", weeks: "Week 7", phase: "Govern", title: "Turn principles into decision rights", text: "Connect the EU AI Act, NIST AI RMF, standards, roles, and accountable controls." },
  { number: "04", weeks: "Weeks 8 to 10", phase: "Sustain", title: "Treat resources as architecture constraints", text: "Measure environmental cost and evaluate model size, compression, and quality tails together." },
  { number: "05", weeks: "Weeks 11 to 12", phase: "Operationalise", title: "Make accountability survive deployment", text: "Bound agent authority and translate policy into evidence, monitoring, escalation, and strategy." },
];

const outcomes = [
  "Categorise traditional, generative, and agentic AI risk across the lifecycle.",
  "Apply ethical and regulatory frameworks to organisational decisions.",
  "Evaluate fairness, explainability, and robustness using real evidence.",
  "Measure environmental cost using explicit functional units and boundaries.",
  "Compare compression choices and their social and linguistic trade-offs.",
  "Create governance models aligned to roles, standards, and obligations.",
  "Develop an operational responsible-AI strategy for a complex organisation.",
];

const labMoves = [
  "Audit selection effects and fairness trade-offs in a hiring context.",
  "Anticipate failure before deployment through a structured pre-mortem.",
  "Connect drift, robustness, model evidence, thresholds, and intervention.",
  "Compare energy, quality, fairness, and compression within valid model families.",
  "Assemble governance artefacts that make policy operational.",
];

export default function ProgrammePage() {
  return (
    <main id="main-content">
      <Breadcrumbs items={[{ label: "Programme" }]} />

      <section className="programme-hero">
        <div className="shell programme-hero-grid">
          <div>
            <p className="eyebrow">Responsible and Ethical AI microcredential</p>
            <h1>From critical judgement to accountable AI operations</h1>
            <p className="lede">A practitioner-led learning journey through risk, evidence, governance, sustainability, and deployment. Every stage ends in a decision learners must be able to defend.</p>
            <div className="button-row">
              <Link className="button primary" to="/course">Enter the course area</Link>
              <Link className="button secondary" to="/about">See how it is taught</Link>
            </div>
          </div>
          <aside className="programme-promise" aria-label="Programme structure">
            <p className="eyebrow">The learning contract</p>
            <strong>12 core weeks</strong>
            <span>Five applied labs · seven assessed outcomes</span>
            <ol>
              <li>Understand</li><li>Assess</li><li>Govern</li><li>Sustain</li><li>Operationalise</li>
            </ol>
            <small>Weeks 13 to 14 are an optional advanced extension pending stackability approval.</small>
          </aside>
        </div>
      </section>

      <section className="shell programme-summary" aria-label="Programme facts">
        <dl className="stat-grid">
          <div><dt>Delivery</dt><dd>{course.schedule}<small>{course.delivery_platform}</small></dd></div>
          <div><dt>Core award scope</dt><dd>12 teaching weeks<small>Proposed 5 ECTS, pending validation</small></dd></div>
          <div><dt>Applied work</dt><dd>Five labs<small>65 percent of core assessment</small></dd></div>
          <div><dt>Participation</dt><dd>Individually evidenced<small>35 percent, including group briefings</small></dd></div>
        </dl>
        <RegulatoryBanner />
      </section>

      <section className="section shell" aria-labelledby="journey-title">
        <div className="split-heading"><div><p className="eyebrow">The learning journey</p><h2 id="journey-title">One accumulating architecture practice</h2></div><p>The course moves from discernment to evidence and then into operating responsibility. The sequence is cumulative rather than a catalogue of ethics topics.</p></div>
        <div className="journey-grid">
          {journey.map((item) => <article key={item.number}><div><span>{item.number}</span><small>{item.weeks}</small></div><p>{item.phase}</p><h3>{item.title}</h3><div>{item.text}</div></article>)}
        </div>
        <p className="extension-note"><strong>Advanced extension · Weeks 13 to 14</strong><span>Strategy development, peer review, future frontiers, and final presentations sit outside the proposed core award until separately approved.</span></p>
        <details className="programme-details schedule-details">
          <summary>View the complete week-by-week schedule</summary>
          <div className="table-scroll">
            <table className="programme-table">
              <caption>Responsible and Ethical AI weekly programme outline</caption>
              <thead><tr><th scope="col">Week</th><th scope="col">Phase</th><th scope="col">Topic and purpose</th><th scope="col">Applied milestone</th></tr></thead>
              <tbody>{weeks.map((week) => <tr key={week.slug}><th scope="row">{week.number === 0 ? "Pre-course" : week.number}{week.number > 12 ? <small>Optional extension</small> : null}</th><td data-label="Phase"><span className={`phase-tag phase-${week.phase.toLowerCase()}`}>{week.phase}</span></td><td data-label="Topic and purpose"><strong>{week.title}</strong><span>{week.narrative}</span></td><td data-label="Applied milestone">{week.labEvent || week.presentation || "Orientation"}</td></tr>)}</tbody>
            </table>
          </div>
        </details>
      </section>

      <section className="section paper-section" aria-labelledby="labs-title">
        <div className="shell">
          <div className="split-heading"><div><p className="eyebrow">Applied assessment</p><h2 id="labs-title">Five labs build one evidence trail</h2></div><p>Each lab adds a different form of defensible evidence. Together they move from model behaviour to operating governance.</p></div>
          <div className="lab-overview-grid">{labs.map((lab) => <article key={lab.slug}><span>Lab {String(lab.number).padStart(2, "0")}</span><h3>{lab.title}</h3><p>{labMoves[lab.number - 1]}</p></article>)}</div>
        </div>
      </section>

      <section className="section shell" aria-labelledby="outcomes-title">
        <div className="split-heading"><div><p className="eyebrow">Seven assessed outcomes</p><h2 id="outcomes-title">Capability that can be evidenced</h2></div><p>Every intended learning outcome is tied to a named artefact and an individually attributable evidence stream.</p></div>
        <ol className="outcome-grid">{outcomes.map((outcome, index) => <li key={outcome}><span>{String(index + 1).padStart(2, "0")}</span><p>{outcome}</p></li>)}</ol>
        <details className="programme-details">
          <summary>Review the complete ILO assessment blueprint</summary>
          <DocumentContent html={content.iloHtml} />
        </details>
      </section>

      <section className="section ink-section" aria-labelledby="reading-title">
        <div className="shell">
          <div className="split-heading"><div><p className="eyebrow">Reading design</p><h2 id="reading-title">Read for the decision ahead</h2></div><p>Preparation is intentionally tiered so essential reading stays achievable while deeper scholarship remains available.</p></div>
          <div className="reading-tier-grid">
            <article><span>01</span><h3>Core</h3><strong>30 to 45 minutes</strong><p>The source every learner needs for the week’s central judgement.</p></article>
            <article><span>02</span><h3>Applied</h3><strong>Case, framework, or dataset</strong><p>The evidence used directly in the activity or lab.</p></article>
            <article><span>03</span><h3>Extended</h3><strong>Optional depth</strong><p>Full chapters and scholarship for learners who want to go further.</p></article>
          </div>
          <div className="reading-actions"><Link className="button light" to="/resources">Browse books and resources</Link></div>
          <details className="programme-details inverted-details">
            <summary>Explore the full 14-week reading map</summary>
            <DocumentContent html={content.readingMapHtml} />
          </details>
        </div>
      </section>

      <section className="section shell public-document" aria-labelledby="specification-title">
        <div className="split-heading"><div><p className="eyebrow">Validation and specification</p><h2 id="specification-title">What is fixed and what remains pending</h2></div><p>The academic design is explicit about the boundary between a teaching-ready package and institutional approval.</p></div>
        <aside className="status-note"><strong>Validation status</strong><p>Proposed credit, NFQ level, accommodations, reassessment, moderation, module ownership, and final contact details remain under TU Dublin authority until formally approved.</p></aside>
        <details className="syllabus-details"><summary>Read the complete release candidate syllabus</summary><DocumentContent html={content.syllabusHtml} /></details>
        <details className="syllabus-details"><summary>Read the course specification status note</summary><DocumentContent html={content.statusHtml} /></details>
      </section>
    </main>
  );
}
